import { useState, useEffect } from 'react'

/**
 * Hook para debounce de valores - evita chamadas excessivas à API durante digitação
 * @param value Valor a ser debounced
 * @param delay Tempo de espera em ms (default: 300ms)
 * @returns Valor debounced
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

/**
 * Hook para debounce de callbacks - evita execução frequente de funções
 * @param callback Função a ser debounced
 * @param delay Tempo de espera em ms (default: 300ms)
 * @returns Função debounced
 */
export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number = 300
): T {
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null)

  const debouncedCallback = ((...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer)
    }
    setTimer(setTimeout(() => {
      callback(...args)
    }, delay))
  }) as T

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [timer])

  return debouncedCallback
}
